import { useEffect, useRef, useState, type JSX } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { usePermissions } from "../auth/PermissionsContext";
import { useCurrentLocation } from "../locations/LocationContext";
import { OrgSwitcher, useCurrentOrg } from "../locations/OrgContext";
import { useCreateLocation } from "../api/locations";
import { errorMessage } from "../api/client";
import { useToast } from "./Toast";
import { Modal } from "./Modal";
import { TextField } from "./TextField";
import { Button } from "./Button";
import {
  BookIcon,
  ClockIcon,
  FolderIcon,
  HomeIcon,
  PlusIcon,
  ShieldIcon,
  UsersIcon,
} from "./icons";
import { useVersionCheck } from "../hooks/useVersionCheck";
import { UserMenu } from "./UserMenu";
import { NotificationsBell } from "./NotificationsBell";
import { BugReportButton } from "./BugReportButton";

interface NavItem {
  to: string;
  /** i18n key under `nav.*`. */
  labelKey: string;
  /** Minimum role needed to see this item. */
  visibility?: "everyone" | "advanced" | "admin";
}

/** Route → inline-SVG glyph. Glyphs are decorative; the label carries the meaning. */
const NAV_ICONS: Record<string, (p: { className?: string }) => JSX.Element> = {
  "/": HomeIcon,
  "/new": PlusIcon,
  "/generate/jobs": ClockIcon,
  "/formats": FolderIcon,
  "/configs": FolderIcon,
  "/specs": ShieldIcon,
  "/setup": BookIcon,
  "/admin": UsersIcon,
};

/** A primary nav link with its inline-SVG glyph (decorative; label carries meaning). */
function NavItemLink({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const Icon = NAV_ICONS[item.to];
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
    >
      {Icon && <Icon />}
      <span>{t(item.labelKey)}</span>
    </NavLink>
  );
}

/**
 * Primary nav, restructured around the job-to-be-done (UX-REVIEW §3.3/§3.4).
 *
 * - Everyone: Home, Create a deliverable, History.
 * - QC + Admin ("advanced" surfaces): Formats, Standards. With only admin/tech roles,
 *   these go to admins (techs keep the simple two-item path).
 * - Admin only: Team, Connector.
 *
 * All labels are plain language driven through i18n. The old power tools
 * (Format library, Combine formats, Build with AI, Multiple work orders) move
 * under an "Advanced" disclosure so every existing route stays reachable.
 */
const PRIMARY_NAV: NavItem[] = [
  { to: "/", labelKey: "nav.home", visibility: "everyone" },
  // The two create actions ("Create EDD file" → /new, "Create a new EDD template" → modal) live as
  // dedicated buttons next to the brand, so they're NOT duplicated here as a nav link.
  // "Your EDD Formats" is the unified view of ALL 196 formats (custom + standard library
  // + site-custom). Visible to everyone (UX fix §27) — it's the answer to "where are my EDDs?".
  { to: "/formats", labelKey: "nav.formats", visibility: "everyone" },
  { to: "/generate/jobs", labelKey: "nav.history", visibility: "everyone" },
  { to: "/specs", labelKey: "nav.standards", visibility: "advanced" },
  { to: "/setup", labelKey: "nav.setup", visibility: "admin" },
  { to: "/admin", labelKey: "nav.team", visibility: "admin" },
];

/** Reachable-but-out-of-the-way tools. */
const ADVANCED_NAV: NavItem[] = [
  { to: "/getting-started", labelKey: "nav.gettingStarted" },
  { to: "/configs", labelKey: "nav.savedFormats" },
  { to: "/code-books", labelKey: "nav.codeBooks" },
  { to: "/templates", labelKey: "nav.formatLibrary" },
  { to: "/merge", labelKey: "nav.combineFormats" },
  { to: "/batch", labelKey: "nav.multipleWorkOrders" },
  { to: "/generate", labelKey: "nav.generate" },
  { to: "/profile", labelKey: "nav.profile" },
];

/** Phase 5/6/7 surfaces. Plain English (English-first; i18n added later) so the EN/ES key-parity
 *  contract isn't affected. `admin`-flagged items render only for admins. */
const PHASE567_NAV: { to: string; label: string; admin?: boolean }[] = [
  { to: "/rejections", label: "Rejections" },
  { to: "/marketplace", label: "Template marketplace" },
  { to: "/analytics", label: "Generation analytics", admin: true },
  { to: "/admin/ai-usage", label: "AI usage", admin: true },
  { to: "/org", label: "Organization", admin: true },
];

/**
 * Compact location switcher. Shows the current location name, lists the org's
 * locations, and offers "+ Add location" which opens a small modal → POST /locations.
 * Every formats/configs/upload call reads the current location from LocationContext.
 */
function LocationSwitcher() {
  const { t } = useTranslation();
  const toast = useToast();
  const { locations, current, setCurrentId, isLoading } = useCurrentLocation();
  const createLocation = useCreateLocation();

  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const submitAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const created = await createLocation.mutateAsync({
        name: trimmed,
        code: code.trim() || undefined,
      });
      setCurrentId(created.id);
      toast.success(t("location.added", { name: created.name }));
      setAddOpen(false);
      setName("");
      setCode("");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // Until locations load there's nothing to switch; render a quiet placeholder.
  if (isLoading && !current) {
    return (
      <span className="faint" aria-label={t("location.label")}>
        {t("location.loading")}
      </span>
    );
  }

  return (
    <div className="location-switch" ref={ref}>
      <button
        type="button"
        className={`location-switch__btn ${open ? "location-switch__btn--open" : ""}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("location.label")}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="location-switch__icon" aria-hidden="true">
          ◉
        </span>
        <span className="location-switch__name">
          {current ? current.name : t("location.none")}
        </span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="location-switch__menu" role="menu">
          {locations.map((loc) => (
            <button
              key={loc.id}
              type="button"
              role="menuitemradio"
              aria-checked={current?.id === loc.id}
              className={`location-switch__item ${
                current?.id === loc.id ? "location-switch__item--active" : ""
              }`.trim()}
              onClick={() => {
                setCurrentId(loc.id);
                setOpen(false);
              }}
            >
              <span>{loc.name}</span>
              {loc.is_default && (
                <span className="faint location-switch__tag">{t("location.default")}</span>
              )}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className="location-switch__item location-switch__item--add"
            onClick={() => {
              setOpen(false);
              setAddOpen(true);
            }}
          >
            {t("location.add")}
          </button>
        </div>
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t("location.addTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              loading={createLocation.isPending}
              disabled={!name.trim()}
              onClick={() => void submitAdd()}
            >
              {t("location.create")}
            </Button>
          </>
        }
      >
        <TextField
          label={t("location.nameLabel")}
          placeholder={t("location.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <TextField
          label={t("location.codeLabel")}
          hint={t("location.codeHint")}
          placeholder={t("location.codePlaceholder")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </Modal>
    </div>
  );
}

/** "Advanced" disclosure: tucks the power tools out of the primary path. */
function AdvancedMenu({ isAdmin, isSuperAdmin }: { isAdmin: boolean; isSuperAdmin: boolean }) {
  const { t } = useTranslation();
  const { canAccess, slugForPath } = usePermissions();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isNavAllowed = (to: string): boolean => {
    const slug = slugForPath(to);
    if (!slug) return true; // no slug mapping → always show
    return canAccess(slug);
  };

  return (
    <div className="nav__advanced" ref={ref}>
      <button
        type="button"
        className={`nav__link ${open ? "nav__link--active" : ""}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t("nav.advanced")} ▾
      </button>
      {open && (
        <div className="nav__advanced-menu" role="menu">
          {ADVANCED_NAV.filter((item) => isNavAllowed(item.to)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              role="menuitem"
              className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
              onClick={() => setOpen(false)}
            >
              {t(item.labelKey)}
            </NavLink>
          ))}
          <span className="nav__divider" aria-hidden="true" />
          {PHASE567_NAV.filter(
            (item) => (!item.admin || isAdmin) && isNavAllowed(item.to),
          ).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              role="menuitem"
              className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          {/* Members — manage who belongs to the active lab + their per-lab role. In the isAdmin
              block (NOT isSuperAdmin) so every lab admin sees it for their own lab (Phase 25). */}
          {isAdmin && (
            <NavLink
              to="/admin/members"
              role="menuitem"
              className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
              onClick={() => setOpen(false)}
            >
              {t("members.nav.label")}
            </NavLink>
          )}
          {isSuperAdmin && (
            <>
              <span className="nav__divider" aria-hidden="true" />
              <NavLink
                to="/admin/accounts"
                role="menuitem"
                className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
                onClick={() => setOpen(false)}
              >
                Accounts
              </NavLink>
              <NavLink
                to="/admin/page-access"
                role="menuitem"
                className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
                onClick={() => setOpen(false)}
              >
                Page Access
              </NavLink>
              <NavLink
                to="/admin/orgs"
                role="menuitem"
                className={({ isActive }) => `nav__link ${isActive ? "nav__link--active" : ""}`.trim()}
                onClick={() => setOpen(false)}
              >
                {t("sadmin.nav.allOrgs")}
              </NavLink>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { canAccess, slugForPath, isSuperAdmin } = usePermissions();
  const { memberships, activeOrgId, isActingInForeignLab, activeOrg, exitToHomeOrg } =
    useCurrentOrg();
  // `isAdmin` is per-LAB (Phase 25): it's the caller's role in the ACTIVE membership, not the global
  // User.role — a user can be admin in one lab and tech in another. Super-admins are admins anywhere.
  const activeMembership = memberships.find((m) => m.org_id === activeOrgId);
  const isAdmin = activeMembership?.role === "admin" || isSuperAdmin;
  const updateAvailable = useVersionCheck();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the mobile nav drawer whenever the route changes (tapped a link).
  useEffect(() => setMobileNavOpen(false), [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Check both role-based visibility AND page-access permissions.
  const isPrimaryAllowed = (item: NavItem): boolean => {
    const slug = slugForPath(item.to);
    if (slug && !canAccess(slug)) return false;
    return true;
  };

  // The primary group is the calm everyone-path (Home / Create / History).
  const primaryItems = PRIMARY_NAV.filter(
    (item) => item.visibility === "everyone" && isPrimaryAllowed(item),
  );
  // The admin group (Formats, Standards, Set up, Team, Connector) only shows to admins.
  // With only admin/tech roles, "advanced" (QC) surfaces also live with admins; techs
  // keep the two-item primary path.
  const adminItems = isAdmin
    ? PRIMARY_NAV.filter(
        (item) =>
          (item.visibility === "admin" || item.visibility === "advanced") &&
          isPrimaryAllowed(item),
      )
    : [];

  return (
    <div className="app-shell">
      {updateAvailable && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "var(--space-3)",
            padding: "var(--space-2)",
            background: "var(--color-primary)",
            color: "#fff",
            fontSize: "0.9em",
          }}
        >
          <span>A new version of Prisma EDD Builder is available.</span>
          <Button size="sm" variant="ghost" onClick={() => window.location.reload()}>
            Reload now
          </Button>
        </div>
      )}
      <nav className="nav">
        <NavLink to="/" className="nav__brand" aria-label="Prisma EDD Builder">
          {/* Wordmark only — no logo glyph yet. Two-tone per the PrismaLIMS lockup
              (navy "Prisma" + blue accent). */}
          <span className="nav__brand-word">Prisma</span>
          <span className="nav__brand-word nav__brand-word--accent">EDD Builder</span>
        </NavLink>
        {/* Hamburger — collapses the create actions + nav links into a drawer on small screens. */}
        <button
          type="button"
          className="nav__burger"
          aria-label={t("nav.menu")}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((v) => !v)}
        >
          <span aria-hidden="true">{mobileNavOpen ? "✕" : "☰"}</span>
        </button>
        {/* `nav__collapse` is display:contents on desktop (children flow in the nav row, unchanged);
            on mobile it becomes a dropdown drawer toggled by the hamburger. */}
        <div className={`nav__collapse ${mobileNavOpen ? "nav__collapse--open" : ""}`.trim()}>
          {/* Two distinct create actions: GENERATE a deliverable vs. CREATE a new format/template. */}
          <NavLink to="/new" className="btn btn--primary btn--sm nav__cta">
            <PlusIcon className="btn__icon" />
            {t("nav.createEddFile")}
          </NavLink>
          <NavLink to="/templates/new" className="btn btn--ghost btn--sm nav__cta">
            <PlusIcon className="btn__icon" />
            {t("nav.createEddTemplate")}
          </NavLink>
          <div className="nav__links">
            <div className="nav__group" role="group" aria-label={t("nav.groupPrimary")}>
              {primaryItems.map((item) => (
                <NavItemLink key={item.to} item={item} />
              ))}
            </div>

            {adminItems.length > 0 && (
              <>
                <span className="nav__divider" aria-hidden="true" />
                <div className="nav__group" role="group" aria-label={t("nav.groupAdmin")}>
                  {adminItems.map((item) => (
                    <NavItemLink key={item.to} item={item} />
                  ))}
                </div>
              </>
            )}

            <span className="nav__divider" aria-hidden="true" />
            <AdvancedMenu isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
          </div>
        </div>
        <div className="spacer" />
        {/* Lab switcher — left of the location switcher. Shows for ANY user who belongs to more than
            one lab (Phase 25), not just super-admins (was `isSuperAdmin` in Phase 24). */}
        {memberships.length > 1 && <OrgSwitcher />}
        {/* Only admins switch locations; techs are pinned to their one location (Phase 6.6). */}
        {isAdmin && <LocationSwitcher />}
        {/* New-signup + future in-app notifications (admins are the recipients today). */}
        {isAdmin && <NotificationsBell />}
        {/* Report a problem — anyone, from anywhere. Captures a screenshot + recent context. */}
        <BugReportButton />
        {/* Theme + language + version + log out live under the user's name. */}
        <UserMenu onLogout={handleLogout} />
      </nav>
      {/* Acting-in banner (super-admin cross-lab) — sticky below the nav, shown ONLY when the active
          lab differs from the home lab. Warning (not danger): an intentional operating context, not an
          error. No dismiss — it persists until the super-admin exits back to their home lab. */}
      {isActingInForeignLab && activeOrg && (
        <div className="acting-in-banner" role="alert" aria-live="polite">
          <span className="acting-in-banner__label">
            {t("sadmin.actingIn.label")}{" "}
            <span className="acting-in-banner__org">{activeOrg.name}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={() => void exitToHomeOrg()}>
            {t("sadmin.actingIn.exit")}
          </Button>
        </div>
      )}
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>
      <footer className="app-footer faint">
        <span className="app-footer__brand">{t("nav.brand")}</span>
        <span aria-hidden="true">·</span>
        <span>{t("footer.copyright")}</span>
      </footer>
    </div>
  );
}
